import type { AgGridReact } from "@jsq/ds/core";
import { URL_FILTER_FIELDS, URL_PARAMS, WORKFLOWS_ROUTE } from "../../constants";
import { buildIndividualUrlFilters } from "../../filterUtils";
import {
  RiskCellRenderer,
  UserCellRenderer,
  NumericCellRenderer,
  WorkflowTypeChips,
} from "./cellRenderers";
import * as styles from "./styles_vanilla.css";
import { IndividualRowItem } from "./types";

/**
 * Returns the column definitions for the Individuals table.
 *
 * @param useWorkflowEngine - When true (crltWorkflowEngine flag ON) filter keys and values
 *   are formatted for the unified WorkflowEngine. When false, the legacy Jira grid format
 *   is used. This value is threaded into cellRendererParams so that click-through navigation
 *   builds the correct URL filter model without requiring feature-flag access in the renderers.
 */
export const getColumnDefs = (
  useWorkflowEngine: boolean,
): AgGridReact.AgGridReactProps["columnDefs"] => {
  const buildWorkflowTypeNavigationUrl = (
    data: IndividualRowItem,
    type: { name: string; value: string },
  ): string | null => {
    if (!data?.assigneeName) return null;
    const workflowTypeKey = useWorkflowEngine
      ? URL_FILTER_FIELDS.WORKFLOW_TYPE_WE
      : URL_FILTER_FIELDS.WORKFLOW_TYPE;
    const typeFilterValue = useWorkflowEngine ? type.value : type.name;
    const filterModel = {
      ...buildIndividualUrlFilters(data, useWorkflowEngine),
      [workflowTypeKey]: { filterType: "set", values: [typeFilterValue] },
    };
    const params = new URLSearchParams();
    params.set(URL_PARAMS.FILTERS_PARAM, JSON.stringify(filterModel));
    return `${WORKFLOWS_ROUTE}?${params.toString()}`;
  };

  return [
    {
      field: "assigneeName",
      headerName: "User",
      sortable: true,
      filter: false,
      width: 400,
      cellRenderer: UserCellRenderer,
    },
    {
      field: "activeWorkflows",
      headerName: "Active workflows",
      sortable: true,
      filter: false,
      width: 160,
      cellRenderer: NumericCellRenderer,
      cellRendererParams: { isActiveWorkflows: true, useWorkflowEngine },
      cellClass: styles.activeWorkflowsBorder,
    },
    {
      field: "workflowTypes",
      headerName: "Workflow types",
      flex: 1,
      minWidth: 400,
      cellRenderer: WorkflowTypeChips,
      cellRendererParams: { buildNavigationUrl: buildWorkflowTypeNavigationUrl },
      sortable: false,
      filter: false,
      autoHeight: true,
      wrapText: true,
    },
    {
      field: "due02Days",
      headerName: "Due in 0-2 days",
      sortable: true,
      sort: "desc" as const,
      filter: false,
      width: 160,
      cellRenderer: RiskCellRenderer,
      cellRendererParams: { dueDateRangeKey: "due02Days", useWorkflowEngine },
    },
    {
      field: "due35Days",
      headerName: "Due in 3-5 days",
      sortable: true,
      filter: false,
      width: 160,
      cellRenderer: RiskCellRenderer,
      cellRendererParams: { dueDateRangeKey: "due35Days", useWorkflowEngine },
    },
    {
      field: "due614Days",
      headerName: "Due in 6-14 days",
      sortable: true,
      filter: false,
      width: 160,
      cellRenderer: RiskCellRenderer,
      cellRendererParams: { dueDateRangeKey: "due614Days", useWorkflowEngine },
    },
    {
      field: "due15PlusDays",
      headerName: "Due in 15+ days",
      sortable: true,
      filter: false,
      width: 160,
      cellRenderer: NumericCellRenderer,
      cellRendererParams: { dueDateRangeKey: "due15PlusDays", useWorkflowEngine },
    },
  ];
};
